'use client';

import { useState } from 'react';
import {
  IconChevronDown,
  IconChevronRight,
  IconFolder,
} from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { unwrapLambdaResponse } from '@/lib/api';
import { useAccessToken } from '@/lib/auth-client';
import { BACKEND_URL } from '@/lib/constants';
import { cn } from '@/lib/utils';
import { useSpaces } from '@/components/spaces/use-spaces';
import type { Folder } from './folder-select-items';

// A destination = a space root (folderId null) or a folder within it.
export interface PickerTarget {
  spaceId: string;
  folderId: string | null;
}

function sameTarget(a: PickerTarget | null, b: PickerTarget): boolean {
  return !!a && a.spaceId === b.spaceId && a.folderId === b.folderId;
}

interface RowProps {
  target: PickerTarget;
  label: string;
  level: number;
  selected: boolean;
  disabled?: boolean;
  expandable: boolean;
  open?: boolean;
  onToggle?: () => void;
  onSelect: () => void;
}

function PickerRow({
  target,
  label,
  level,
  selected,
  disabled,
  expandable,
  open,
  onToggle,
  onSelect,
}: RowProps) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onSelect}
      className={cn(
        'flex h-8 w-full items-center gap-1.5 rounded-md pr-2 text-base transition-colors',
        selected
          ? 'bg-wash-light font-medium text-fg'
          : 'text-fg-secondary hover:bg-wash-lighter',
        disabled && 'cursor-not-allowed opacity-50'
      )}
      style={{ paddingLeft: `${level * 16 + 8}px` }}
    >
      <span
        role={expandable ? 'button' : undefined}
        aria-label={expandable ? (open ? 'Collapse' : 'Expand') : undefined}
        className="flex size-4 shrink-0 items-center justify-center text-fg-light"
        onClick={
          expandable
            ? (e) => {
                e.stopPropagation();
                onToggle?.();
              }
            : undefined
        }
      >
        {expandable ? (
          open ? (
            <IconChevronDown className="size-3.5" />
          ) : (
            <IconChevronRight className="size-3.5" />
          )
        ) : null}
      </span>
      <IconFolder className="size-4 shrink-0 text-fg-tertiary" />
      <span className="truncate">{label}</span>
    </button>
  );
}

function FolderBranch({
  spaceId,
  folder,
  childrenOf,
  level,
  value,
  onChange,
  disabledFolderId,
}: {
  spaceId: string;
  folder: Folder;
  childrenOf: (parentId: string | null) => Folder[];
  level: number;
  value: PickerTarget | null;
  onChange: (t: PickerTarget) => void;
  disabledFolderId?: string;
}) {
  const [open, setOpen] = useState(false);
  const children = childrenOf(folder.id);
  const target: PickerTarget = { spaceId, folderId: folder.id };
  return (
    <div>
      <PickerRow
        target={target}
        label={folder.name}
        level={level}
        selected={sameTarget(value, target)}
        disabled={folder.id === disabledFolderId}
        expandable={children.length > 0}
        open={open}
        onToggle={() => setOpen((v) => !v)}
        onSelect={() => onChange(target)}
      />
      {open &&
        children.map((child) => (
          <FolderBranch
            key={child.id}
            spaceId={spaceId}
            folder={child}
            childrenOf={childrenOf}
            level={level + 1}
            value={value}
            onChange={onChange}
            disabledFolderId={disabledFolderId}
          />
        ))}
    </div>
  );
}

function SpaceBranch({
  spaceId,
  spaceName,
  defaultOpen,
  value,
  onChange,
  disabledFolderId,
}: {
  spaceId: string;
  spaceName: string;
  defaultOpen: boolean;
  value: PickerTarget | null;
  onChange: (t: PickerTarget) => void;
  disabledFolderId?: string;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const { getAccessToken, loading } = useAccessToken();
  const { data, isLoading } = useQuery({
    queryKey: ['space', spaceId, 'files', 'folders'],
    queryFn: async () => {
      const token = await getAccessToken();
      if (!token) throw new Error('Not authenticated');
      const res = await fetch(`${BACKEND_URL}/spaces/${spaceId}/files/folders`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to load folders');
      return unwrapLambdaResponse<{ allFolders: Folder[] }>(await res.json());
    },
    enabled: open && !loading,
  });

  const all = data?.allFolders ?? [];
  const childrenOf = (parentId: string | null) =>
    all
      .filter((f) => (f.parentId ?? null) === parentId)
      .sort((a, b) => a.indexNumber.localeCompare(b.indexNumber));

  const root: PickerTarget = { spaceId, folderId: null };
  return (
    <div>
      <PickerRow
        target={root}
        label={spaceName}
        level={0}
        selected={sameTarget(value, root)}
        expandable
        open={open}
        onToggle={() => setOpen((v) => !v)}
        onSelect={() => onChange(root)}
      />
      {open &&
        (isLoading ? (
          <div className="py-1 pl-8 pr-2">
            <Skeleton className="h-5 w-2/3" />
          </div>
        ) : (
          childrenOf(null).map((folder) => (
            <FolderBranch
              key={folder.id}
              spaceId={spaceId}
              folder={folder}
              childrenOf={childrenOf}
              level={1}
              value={value}
              onChange={onChange}
              disabledFolderId={disabledFolderId}
            />
          ))
        ))}
    </div>
  );
}

// All writable spaces as roots, each expandable to its folder tree. Used by the
// New Folder and Move dialogs so a destination can be anywhere the user can
// edit.
export function SpaceFolderPicker({
  value,
  onChange,
  defaultOpenSpaceId,
  disabledFolderId,
}: {
  value: PickerTarget | null;
  onChange: (target: PickerTarget) => void;
  defaultOpenSpaceId?: string;
  disabledFolderId?: string;
}) {
  const { data: spaces, isLoading } = useSpaces();
  const writable = (spaces ?? []).filter(
    (s) => s.currentUserRole !== 'viewer'
  );

  if (isLoading) {
    return (
      <div className="flex flex-col gap-2 p-2">
        <Skeleton className="h-6 w-full" />
        <Skeleton className="h-6 w-2/3" />
      </div>
    );
  }

  return (
    <div className="max-h-64 overflow-auto rounded-md border border-line p-1">
      {writable.map((space) => (
        <SpaceBranch
          key={space.id}
          spaceId={space.id}
          spaceName={space.name}
          defaultOpen={space.id === defaultOpenSpaceId}
          value={value}
          onChange={onChange}
          disabledFolderId={disabledFolderId}
        />
      ))}
    </div>
  );
}

// Move-documents dialog: pick any space/folder the user can edit.
// subjectLabel is pre-formatted by the caller (a quoted name or "8 items").
// initialTarget preselects the subjects' current location; null (e.g. a
// mixed-folder selection) keeps Move disabled until an explicit pick, so
// confirming can't silently relocate items to the space root.
export function MoveToSpaceDialog({
  open,
  onOpenChange,
  subjectLabel,
  initialTarget,
  defaultOpenSpaceId,
  onMove,
  isMoving,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  subjectLabel: string;
  initialTarget: PickerTarget | null;
  defaultOpenSpaceId?: string;
  onMove: (target: PickerTarget) => void;
  isMoving: boolean;
}) {
  const [target, setTarget] = useState<PickerTarget | null>(null);

  // Reset the selection each time the dialog opens for a new subject.
  const initial = initialTarget;

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o);
        if (!o) setTarget(null);
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Move {subjectLabel}</DialogTitle>
          <DialogDescription>
            Choose a space or folder to move into.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <SpaceFolderPicker
            value={target ?? initial}
            onChange={setTarget}
            defaultOpenSpaceId={defaultOpenSpaceId}
          />
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={isMoving || !(target ?? initial)}
            onClick={() => {
              const t = target ?? initial;
              if (t) onMove(t);
            }}
          >
            {isMoving ? 'Moving…' : 'Move'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
